<template>
	<Modal v-if="shown"
		   :title="this.realTitle"
		   @close="show(false)"
		   :close-on-click-outside="false"
		   :width="600"
	>
		<template v-slot:body>
			<div class="modal-content">
				<form class="flex flex_column">
					<div class="margin_top">
						<div>
							<AvailableDebtorSelect
								:value="debtorId"
								:api="loadAvailableDebtors"
								v-on:input="changeDebtor"
								:allow-add-debtor="true"
								:required="true"
								:disable="disabled"
								data-vv-name="debtorId"
								v-validate="modelValidations.debtorId"
								:error="errors.first('debtorId')"
								ref="availableDebtorSelect"
							></AvailableDebtorSelect>
						</div>
					</div>
					<div class="flex">
						<md-field class="margin_r" displayOnly>
							<label>{{$t("common.applicableLaw")}}</label>
							<md-input :value="applicableLaw" disabled></md-input>
						</md-field>
						<md-field displayOnly>
							<label>{{$t("revolvingPutOption.paymentTerms")}}</label>
							<md-input :value="paymentTerm" disabled></md-input>
						</md-field>
					</div>
					<div class="flex">
						<DatePicker
							class="flex_1"
							style="margin-right: 1rem"
							v-model="form.startDate"
							:placeholder="$t('revolvingPutOption.salesPeriod.start')"
							:disabled="disabled"
							:min-date="config ? config.minOptionStartDate : null"
							:max-date="form.endDate"
							required
							data-vv-name="startDate"
							v-validate="modelValidations.startDate"
							:error="errors.first('startDate')"
						></DatePicker>
						<DatePicker
							class="flex_1"
							v-model="form.endDate"
							:placeholder="$t('revolvingPutOption.salesPeriod.end')"
							:disabled="disabled"
							:min-date="form.startDate || (config ? config.minOptionStartDate : null)"
							required
							data-vv-name="endDate"
							v-validate="modelValidations.endDate"
							:error="errors.first('endDate')"
						></DatePicker>
					</div>
					<div class="flex">
						<md-field class="flex_1 margin_r" :class="[{ 'md-error': errors.has('saleLimit')}]">
							<label>{{ $t("revolvingPutOption.revolvingLine") }}</label>
							<MoneyAmountInput v-model="form.saleLimit"
											  :required="true"
											  :disabled="disabled"
											  data-vv-name="saleLimit"
											  v-validate="modelValidations.saleLimit"
							></MoneyAmountInput>
							<span v-if="errors.has('saleLimit')" class="validation-error">{{ errors.first('saleLimit') }}</span>
							<el-tooltip :open-delay="300" placement="top"
										:content="$t('revolvingPutOption.table.header.revolvingLine.tooltip')">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-field>
						<md-field class="flex_1">
							<label>{{ $t("revolvingPutOption.estimatedSalesVolume") }}</label>
							<MoneyAmountInput v-model="form.estimatedSalesVolume"
											  :disabled="disabled"
							></MoneyAmountInput>
							<el-tooltip :open-delay="300" placement="top"
										:content="$t('revolvingPutOption.table.header.estimatedSalesVolume.tooltip')">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-field>
					</div>
					<div class="flex">
						<currency-select
							class="flex_1 margin_r"
							v-model="form.currency"
							:isError="!form.currency"
							:disabled="disabled"
							:required="true"
							data-vv-name="currency"
							v-validate="modelValidations.currency"
							:error="errors.first('currency')"
						></currency-select>
						<md-field class="flex_1" :class="[{ 'md-error': errors.has('discountRate')}]">
							<label>{{ $t("revolvingPutOption.discount.withPercent") }}</label>
							<MoneyAmountInput v-model="form.discountRate"
											  :disabled="disabled"
											  :required="true"
											  data-vv-name="discountRate"
											  v-validate="modelValidations.discountRate"
							></MoneyAmountInput>
							<span v-if="errors.has('discountRate')" class="validation-error">{{ errors.first('discountRate') }}</span>
							<el-tooltip :open-delay="300" placement="top"
										:content="$t('revolvingPutOption.table.header.discount.tooltip')">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-field>
					</div>
					<div class="flex">
						<md-field class="flex_1 margin_r" :class="[{ 'md-error': errors.has('disclosed')}]">
							<label>{{ $t("common.assignmentType") }}</label>
							<TbSelect v-model="form.disclosed"
									  :search-allow="false"
									  :options="assignmentOptions"
									  options-value-field="disclosed"
									  :options-display-getter="(item) => $t(item.translationKey)"
									  data-vv-name="disclosed"
									  v-validate="modelValidations.disclosed"
									  required
									  :disabled="disabled"
							/>
							<span v-if="errors.has('disclosed')" class="validation-error">{{ errors.first('disclosed') }}</span>
							<el-tooltip :open-delay="300" placement="top"
										:content="$t('common.disclosed.tooltip')">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-field>
						<DurrationSelect class="flex_1"
										 v-model="form.auctionDurationInHours"
										 required
										 :disabled="disabled"
										 data-vv-name="auctionDurationInHours"
										 v-validate="modelValidations.auctionDurationInHours"
										 :error="errors.first('auctionDurationInHours')"
										 :no-table="true"
						></DurrationSelect>
					</div>
				</form>

				<div v-if="showOffers" class="margin_top">
					<h4 class="info_title small_margin">{{$t('revolvingPutOption.offerList')}}</h4>
					<RevolvingPutOptionOffersTable :offers="form.offers" :can-accept="canAcceptOffers" @accept="confirmAcceptOffer"/>
				</div>
			</div>

			<div class="flex  margin_top" :class="disabled ? 'flex_end' : 'flex_between'">
				<template v-if="disabled">
					<md-button @click="show(false)" class="md-blank">
						{{ $t("common.button.close") }}
					</md-button>
				</template>
				<template v-else>
					<md-button v-if="editMode" @click="confirmDeletePutOption" class="md-block">
						{{ $t("common.button.delete") }}
					</md-button>
					<md-button @click="confirmSavePutOption" class="md-blank">
						{{ $t("common.save") }}
					</md-button>
					<md-button @click="confirmSubmitPutOption" class="md-primary">
						{{ $t("common.button.submit") }}
					</md-button>
				</template>
			</div>
		</template>
	</Modal>
</template>

<script>

import Modal from "src/components/templates/modal/Modal";
import {revolvingPutOptionsSellerApi} from "@/common/api/revolvingPutOptionsSellerApi";
import {validateForm} from "@/validation/validation";
import DatePicker from "@/components/templates/DatePicker";
import CurrencySelect from "@/components/templates/select/CurrencySelect";
import MoneyAmountInput from "@/components/templates/common/MoneyAmountInput";
import enumStatus from "@/common/enum";
import AvailableDebtorSelect from "@/components/templates/seller/AvailableDebtorSelect";
import RevolvingPutOptionOffersTable
	from "@/components/templates/seller/putOption/RevolvingPutOptionOffersTable";
import {enumDef} from "@/common/enumDefs";
import DurrationSelect from "@/components/templates/seller/sell/DurrationSelect";

export default {
	components: {
		Modal,
		AvailableDebtorSelect,
		DatePicker,
		CurrencySelect,
		MoneyAmountInput,
		RevolvingPutOptionOffersTable,
		DurrationSelect
	},
	data() {
		return {
			enumDef: enumDef,
			editMode: false,
			disabled: false,
			shown: false,
			applicableLaw: null,
			paymentTerm: null,
			assignmentOptions: enumStatus.discloseList,
			debtorId: null,
			referenceNumber: null,
			config: null,
			defaultForm: {
				id: null,
				startDate: null,
				endDate: null,
				saleLimit: null,
				currency: null,
				disclosed: null,
				discountRate: null,
				estimatedSalesVolume: null,
				offers: null
			},
			form: {...this.defaultForm},
			modelValidations: {
				debtorId: {
					required: true
				},
				startDate: {
					required: true
				},
				endDate: {
					required: true
				},
				saleLimit: {
					required: true
				},
				currency: {
					required: true
				},
				disclosed: {
					required: true
				},
				discountRate: {
					required: true
				},
				auctionDurationInHours: {
					required: true
				},
			}

		};
	},
	methods: {
		show(value) {
			this.shown = value;
		},
		resetModalData() {
			this.form = {...this.defaultForm}
			this.debtorId = null;
			this.applicableLaw = null;
			this.referenceNumber = null;
			this.paymentTerm = null;
			this.config = null;
			this.disabled = false;
			this.editMode = false;
		},
		showAddPutOptionModal(data) {
			this.show(true);
			this.resetModalData();

			if (data && data.debtorId) {
				this.debtorId = data.debtorId;
			}

			this.loadPutOptionConfig();
		},
		showEditPutOptionModal(data) {
			this.show(true);
			this.resetModalData();
			this.editMode = true;

			this.loadPutOptionConfig();
			this.loadPutOptionDetails(data);
		},
		showPutOptionDetails(data) {
			this.show(true);
			this.resetModalData();
			this.disabled = true;

			this.loadPutOptionDetails(data);
		},
		loadAvailableDebtors() {
			return revolvingPutOptionsSellerApi.inspection.getDebtors();
		},
		loadPutOptionConfig() {
			revolvingPutOptionsSellerApi.inspection.getConfig()
				.then(res => {
					this.config = res;
				}).catch(this.toast);
		},
		loadPutOptionDetails(data) {
			if (data && data.id) {
				revolvingPutOptionsSellerApi.inspection.getDetails(data.id)
					.then(res => {
						this.form = { ...res };
						this.debtorId = res.debtorId;
						this.referenceNumber = res.referenceNumber;
					}).catch(this.toast);
			}
		},
		changeDebtor(data) {
			this.debtorId = data.id;

			if (!data.applicableLaw && !data.historicalPaymentDays) {
				this.toast(this.$t('debtorManagement.revolvingPutOption.putOptionModal.missingDataInBusinessRelation'));
			} else {
				this.applicableLaw = data.applicableLaw;
				this.paymentTerm = data.historicalPaymentDays;
			}
		},
		confirmSavePutOption() {
			validateForm(this, this.$validator).then(() => {
				let option = {
					title: this.$t("debtorManagement.revolvingPutOption.savePutOption.confirm.title"),
					msg: this.$t("debtorManagement.revolvingPutOption.savePutOption.confirm.text"),
					confirmFun: this.savePutOption
				};
				this.confirmMeg(option, true);
			}).catch(this.toast);
		},
		savePutOption() {
			let postData = {
				...this.form,
				debtorId: this.debtorId
			};

			revolvingPutOptionsSellerApi.operation.savePutOption(postData)
				.then(() => {
					this.toastSuccess();
					this.putOptionChanged();

				}).catch(this.toast);
		},
		confirmSubmitPutOption() {
			validateForm(this, this.$validator).then(() => {
				let option = {
					title: this.$t("debtorManagement.revolvingPutOption.publishPutOption.confirm.title"),
					msg: this.$t("debtorManagement.revolvingPutOption.publishPutOption.confirm.text"),
					confirmFun: this.submitPutOption
				};
				this.confirmMeg(option, true);
			}).catch(this.toast);
		},
		submitPutOption() {
			let postData = {
				...this.form,
				debtorId: this.debtorId
			};

			revolvingPutOptionsSellerApi.operation.publishPutOption(postData)
				.then(() => {
					this.toastSuccess();
					this.putOptionChanged();
				}).catch(this.toast);
		},
		confirmDeletePutOption() {
			let option = {
				title: this.$t("debtorManagement.revolvingPutOption.deletePutOption.confirm.title"),
				msg: this.$t("debtorManagement.revolvingPutOption.deletePutOption.confirm.text"),
				confirmFun: this.deletePutOption
			};
			this.confirmMeg(option, true);
		},
		deletePutOption(id) {
			let putOptionId = id ? id : this.form.id;
			revolvingPutOptionsSellerApi.operation.deletePutOption(putOptionId, {})
				.then(() => {
					this.toastSuccess();
					this.putOptionChanged();
				}).catch(this.toast);
		},
		confirmAcceptOffer(data) {
			let option = {
				title: this.$t("debtorManagement.revolvingPutOption.acceptOffer.confirm.title"),
				msg: this.$t("debtorManagement.revolvingPutOption.acceptOffer.confirm.text"),
				confirmFun: this.acceptOffer,
				params: data
			};
			this.confirmMeg(option, true);
		},
		acceptOffer(data) {
			revolvingPutOptionsSellerApi.operation.acceptOffer(data.offerId, data.premium)
				.then(() => {
					this.toastSuccess();
					this.putOptionChanged();
				}).catch(this.toast);
		},
		withdrawPutOption(id) {
			let putOptionId = id ? id : this.form.id;
			revolvingPutOptionsSellerApi.operation.withdrawPutOption(putOptionId, {})
				.then(() => {
					this.toastSuccess();
					this.putOptionChanged();
				}).catch(this.toast);
		},
		putOptionChanged() {
			this.show(false);
			this.$emit('change');
		},
	},
	computed: {
		showOffers() {
			return enumDef.revolvingPutOptionStatus.isPublishedOrReadyToBeAccepted(this.form.status);
		},
		canAcceptOffers() {
			return this.form.status === enumDef.revolvingPutOptionStatus.ReadyToBeAccepted;
		},
		realTitle() {
			return this.$t('common.revolvingPutOption') + (this.referenceNumber ? " " + this.referenceNumber : "");
		}
	}
};
</script>